import { FC, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import {
	Box,
	Button,
	Chip,
	CircularProgress,
	LinearProgress,
	MenuItem,
	OutlinedInput,
	Paper,
	Select,
	Stack,
	Typography,
} from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FunctionsIcon from "@mui/icons-material/Functions";
import dayjs from "dayjs";
import { useUserStore } from "../../../../../stores/user.store";
import { useCurrencySetting } from "../../../../../hooks/useSetting";
import { useDateFormat } from "../../../../../hooks/useDateFormat";
import useProgramActivitySummary from "../../../../../hooks/useProgramActivitySummary";
import { useActivePayers } from "../../../../../hooks/userPayers";
import { useGroupLabels } from "../../../../../hooks/groupLabels";
import { useGetProviderSitesByOUId } from "../../../../../hooks/useBeneficiaries";
import { useAppTheme } from "../../../../../utils/theme";
import BasicDatepicker from "../../../../../components/BasicDatepicker/BasicDatepicker";
import {
	NoProgramCard,
	ExclusionBanner,
	OrgUnitFilterField,
	ProgramFilterField,
	ReportActions,
	ReportTabProps,
	exclusionBannerText,
	groupLabelsForSites,
	payersForPrograms,
	programNameOf,
} from "../common";
import {
	combinedOrgUnitIds,
	hiddenProgramCount,
	optionNameResolver,
	programContextLine,
	sectionContextLine,
	sortProgramBreakdown,
	visitBucketColumns,
	visitRowFor,
	hasAssignedPrograms,
	resolveProgramSelection,
} from "../../../../../utils/programSelection";
import {
	buildProgramSummaryPdfModel,
	renderProgramSummaryPdf,
	type PdfMetric,
} from "../../../../../utils/programSummaryPdf";
import type { ProgramActivitySummaryResponse } from "../../../../../services/reports.service";

interface Option {
	id: string;
	name: string | null;
}

interface Metric {
	label?: string;
	value?: ReactNode;
	color?: string;
	caption?: string;
}

const toOptions = (
	list: ReadonlyArray<{ _id?: string; id?: string; name?: string | null }>,
): Option[] =>
	(list ?? []).map((o) => ({ id: o._id ?? o.id ?? "", name: o.name ?? null }));

/**
 * SIMS-8905 — the transaction caption agrees with its count.
 *
 * SIMS-8038 defined the metric with a fixed caption, so "1 transactions" reached
 * the screen and, through `toPdfMetrics`, the PDF export as well. Only the plural
 * form is conditional; the caption stays a plain string so nothing downstream has
 * to know about pluralisation.
 */
const txnCaption = (count: number): string =>
	count === 1 ? "transaction" : "transactions";

/**
 * A screen Metric flattened to the text the PDF prints (SIMS-8062 / NFR-4).
 *
 * `value` is a ReactNode because the cards render it into JSX; in practice every cell in
 * this tab supplies a string or a number. Anything else would be a component, which has
 * no text form, so it prints empty rather than as "[object Object]".
 *
 * The caption rides along with the value ("12 transactions") because on screen it sits
 * directly under the figure and qualifies it.
 */
const toPdfMetrics = (cells: Metric[]): PdfMetric[] =>
	cells.map((cell) => {
		const value =
			typeof cell.value === "string" || typeof cell.value === "number"
				? String(cell.value)
				: "";
		return {
			label: cell.label ?? "",
			value: cell.caption ? `${value} ${cell.caption}` : value,
		};
	});

const ProgramActivitySummaryTab: FC<ReportTabProps> = ({
	programId,
	programIds,
	programs,
	programsLoading = false,
	onProgramChange,
	onProgramsChange,
}) => {
	const theme = useAppTheme();
	const { userInfo } = useUserStore();
	const accountId = userInfo?.accountId ?? "";
	const currencyMask = useCurrencySetting()?.mask ?? "$";
	// SIMS-7770: date display/entry follows the account's country format setting
	// (US → MM/DD/YYYY, UK → DD/MM/YYYY); JANUS deploys in both.
	const { dateFormat } = useDateFormat();
	// SIMS-9292 (D4): the RAW selection is what the control shows — empty renders as
	// "All programs". The EFFECTIVE set is what the report asks for: empty resolves to
	// every assigned program and is sent explicitly, so the server never receives an
	// empty list from a user who has programs. `programs` is already the caller's
	// assigned set, resolved from userInfo.programs by ReportsDashboard.
	//
	// Resolved HERE, ahead of everything that names or counts the selection, which
	// describes the effective set and never the raw one (empty on the default state).
	const assignedIds = programs.map((p) => p._id);
	const effectivePrograms = resolveProgramSelection(
		programIds ?? [],
		assignedIds,
	);
	// The first effective program — what `programId` (the first SELECTED id) named
	// when every assigned program arrived pre-ticked, before SIMS-9292.
	const programName = programNameOf(programs, effectivePrograms[0] ?? "");

	const [orgUnitIds, setOrgUnitIds] = useState<string[]>([]);
	const [payerIds, setPayerIds] = useState<string[]>([]);
	const [groupLabelIds, setGroupLabelIds] = useState<string[]>([]);
	const [providerId, setProviderId] = useState<string>("");
	// SIMS reports: reuse the same "Exporting…" affordance as the other tabs. The
	// structured export is fast, but jsPDF and autoTable are code-split, so the first
	// click still waits on a chunk. Also blocks double-clicks. PDF-only, so a boolean.
	const [isExporting, setIsExporting] = useState<boolean>(false);
	const [startDate, setStartDate] = useState<string>("");
	const [endDate, setEndDate] = useState<string>("");

	// Org units, payers, group labels and provider are program-specific — clear
	// every scope selection when the program changes so a stale value isn't sent
	// for a different program (e.g. an OU that doesn't belong to the new program).
	useEffect(() => {
		setOrgUnitIds([]);
		setPayerIds([]);
		setGroupLabelIds([]);
		setProviderId("");
	}, [programId]);

	// SIMS-7595: the OU ids the user is actually assigned for this program. Used
	// both to scope the OU dropdown and to constrain the report to the user's OUs
	// when no explicit OU is selected, so totals never include unassigned OUs.
	// SIMS-7990: across ALL selected programs, de-duplicated — not just the first.
	// This feeds both the org-unit options request and the report's default OU filter,
	// so leaving it on a single programId kept the options (and therefore the
	// "Shared across programs" group and its "in N programs" count) limited to one
	// program, and scoped the results to one program as well.
	// Across the EFFECTIVE selection (SIMS-9292, D4) — resolved above.
	const assignedOuIds = combinedOrgUnitIds(
		userInfo?.programs,
		effectivePrograms,
	);

	/**
	 * SIMS-8023 AC1/AC2/AC3: the report runs ONLY on "Run report".
	 *
	 * The filter state above is what the user is editing; `applied` is the snapshot
	 * that was actually run. The query reads `applied`, so editing a filter cannot
	 * trigger a fetch — and clearing `applied` clears the results (AC3).
	 *
	 * `null` means "not run yet": the query hook is gated on a non-empty programId,
	 * so an empty one disables it entirely and nothing is fetched on mount (AC1).
	 */
	const [applied, setApplied] = useState<{
		// SIMS-7990: the snapshot holds EVERY selected program, not the first one.
		// Kept as an array here and serialized to CSV only at the request boundary,
		// so the state stays truthful about what the user selected.
		programIds: string[];
		orgUnitIds?: string;
		payerIds?: string;
		groupLabelIds?: string;
		providerId?: string;
		startDate?: string;
		endDate?: string;
	} | null>(null);

	const runReport = (): void =>
		setApplied({
			programIds: effectivePrograms,
			orgUnitIds:
				(orgUnitIds.length ? orgUnitIds : assignedOuIds).join(",") || undefined,
			payerIds: payerIds.length ? payerIds.join(",") : undefined,
			groupLabelIds: groupLabelIds.length ? groupLabelIds.join(",") : undefined,
			providerId: providerId || undefined,
			startDate: startDate || undefined,
			endDate: endDate || undefined,
		});

	/**
	 * SIMS-8023 AC3 — ANY filter edit invalidates the run.
	 *
	 * Only the org-unit control used to do this, so changing the payer, group label,
	 * provider or either date left the previous run's results on screen, labelled with
	 * filters that no longer matched them (TestRail C15441). Every filter setter now
	 * goes through one of these so a new filter cannot be added without deciding the
	 * question.
	 */
	const clearResults = (): void => setApplied(null);

	const changePayerIds = (v: string[]): void => {
		setPayerIds(v);
		clearResults();
	};
	const changeGroupLabelIds = (v: string[]): void => {
		setGroupLabelIds(v);
		clearResults();
	};
	const changeProviderId = (v: string): void => {
		setProviderId(v);
		clearResults();
	};
	const changeStartDate = (v: string): void => {
		setStartDate(v);
		clearResults();
	};
	const changeEndDate = (v: string): void => {
		setEndDate(v);
		clearResults();
	};

	/**
	 * SIMS-8023 AC3 (Edge) — changing the PROGRAM selection clears the results too.
	 *
	 * The scope-reset effect above keys on `programId`, which is only the FIRST
	 * selected program, so adding or removing any other program left `applied` intact
	 * and the stale results on screen (TestRail C15442).
	 *
	 * Keyed on the joined ids rather than the array: `programIds` is `programByTab[id]
	 * ?? []` in the dashboard, so an absent entry yields a fresh array every render and
	 * an array-identity dependency would clear the results in a loop.
	 */
	const programKey = (programIds ?? []).join(",");
	useEffect(() => {
		setApplied(null);
	}, [programKey]);

	/**
	 * Programs excluded by the org-unit filter (SIMS-8006 AC1 / FR-5).
	 *
	 * Derived entirely client-side from the selection: if an explicit org-unit filter
	 * is applied and none of a selected program's entitled org units is in it, that
	 * program can contribute nothing — so it is not shown, and it is counted here.
	 *
	 * Only counted when an org-unit filter is actually applied: with no filter every
	 * selected program is in scope, so nothing is excluded by this rule.
	 */
	const excludedProgramCount = effectivePrograms.filter((pid) => {
		if (orgUnitIds.length === 0) return false;
		const ous = combinedOrgUnitIds(userInfo?.programs, [pid]);
		return !ous.some((ou) => orgUnitIds.includes(ou));
	}).length;

	const { data, isLoading, isFetching } = useProgramActivitySummary({
		accountId,
		// Empty until Run — this is what keeps the query from firing (AC1).
		// CSV boundary (SIMS-7990): the backend accepts one id or a comma-separated
		// list on the same `programId` param, matching how orgUnitIds/payerIds are
		// already sent. An empty selection joins to "", which is what keeps the query
		// disabled and stops a fetch on mount (AC2 x SIMS-8023 AC1).
		programId: (applied?.programIds ?? []).join(","),
		orgUnitIds: applied?.orgUnitIds,
		payerIds: applied?.payerIds,
		groupLabelIds: applied?.groupLabelIds,
		providerId: applied?.providerId,
		startDate: applied?.startDate,
		endDate: applied?.endDate,
	});

	const { data: payers } = useActivePayers();
	const { data: groupLabels } = useGroupLabels();
	// SIMS-7397: group labels are scoped to the OUs the user has for the selected
	// program (same logic as the Beneficiary Report): resolve the program's OUs,
	// fetch their provider sites, then keep the labels attached to those sites.
	const { data: providerSites } = useGetProviderSitesByOUId({
		ouIds: assignedOuIds,
		queryKey: "sims-reports-provider-sites",
	});

	// SIMS-7595: the OU dropdown must list only the user's assigned OUs for the
	// program (the scoped provider sites), not data.availableSites — which is
	// program-wide and leaks OUs the user isn't assigned to (e.g. "Alternatives").
	const siteOptions = toOptions(providerSites ?? []);
	const providerOptions = toOptions(data?.availableProviders ?? []);
	// SIMS-7396: the Payer filter lists only payers configured for the selected
	// program (program.payers), intersected with the account's active payers.
	// When the program has none, the dropdown is disabled.
	// SIMS-7994 AC3: the union across EVERY selected program, not the first one.
	// `programs.find((p) => p._id === programId)` made the filter a property of
	// whichever program sorted first (TestRail C15460).
	// AC6: dependent filters follow the RESOLVED selection, so "All programs"
	// offers values from every assigned program instead of silently offering none.
	const selectedPrograms = programs.filter((p) =>
		effectivePrograms.includes(p._id),
	);
	const payerOptions = toOptions(payersForPrograms(payers, selectedPrograms));
	const payersDisabled = payerOptions.length === 0;
	const groupLabelOptions = toOptions(
		groupLabelsForSites(groupLabels, providerSites),
	);

	const money = (n: number): string =>
		`${currencyMask}${(n ?? 0).toLocaleString("en-US", {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
		})}`;
	const counts = data?.enrollmentCounts;
	const inc = data?.incentiveSummary;

	// Design tokens → theme palette (driven by the account's themeColors).
	const c = {
		band: theme.palette.surface.main, // tinted section band
		border: theme.palette.outline.light,
		divider: theme.palette.outline.light,
		label: theme.palette.textSecondary.main,
		muted: theme.palette.textSecondary.main,
		ink: theme.palette.textPrimary.main,
		good: theme.palette.secondary.dark, // earned / negative / attended
		bad: theme.palette.error.main, // positive / missed / unexcused
		warn: theme.palette.warning.dark, // excused absence
		blue: theme.palette.info.main, // disbursed money
		bandDark: theme.palette.primary.dark, // Clear all button
		onDark: theme.palette.surface.light, // text on the dark button
	};

	const nameById = (opts: Option[], ids: string[]): string =>
		ids
			.map((id) => opts.find((o) => o.id === id)?.name ?? id)
			.filter(Boolean)
			.join(", ");

	/**
	 * Org-unit names for the per-program context line (SIMS-8889).
	 *
	 * `siteOptions` FIRST, and that is the fix. The line was resolved against
	 * `data.availableSites`, which the backend derives from the enrollments of the
	 * SELECTED programs — so an entitled org unit whose only enrollments sit in a
	 * program the user did not select is absent from it, and the line printed its
	 * raw ObjectId. `siteOptions` is fetched BY ID for the user's assigned org units
	 * (`/organizations/providers-site?ids=…`), so it names every org unit the line
	 * can mention: both `orgUnitIds` and `notApplied` are subsets of `assignedOuIds`.
	 *
	 * `availableSites` stays as the second source for the one case the first cannot
	 * cover — the store's copy of `userInfo.programs` being narrower than the
	 * entitlements the server resolved the line from.
	 *
	 * Same rule as SIMS-7595, which already ruled `availableSites` out for the OU
	 * dropdown; the context line was simply left behind.
	 */
	const orgUnitNameOf = optionNameResolver(
		siteOptions,
		data?.availableSites ?? [],
	);

	/**
	 * The payer / group-label filters the run actually applied (SIMS-8890).
	 *
	 * Read from `applied` — the snapshot Run report took — not from the live filter
	 * state, so the line describes the query behind the numbers beside it. (Editing
	 * either filter clears `applied` and the results with it, so in practice the two
	 * agree; taking the snapshot is what keeps that a consequence rather than a
	 * coincidence.)
	 */
	const appliedNames = (csv: string | undefined, opts: Option[]): string[] => {
		const nameOf = optionNameResolver(opts);
		return (csv ?? "")
			.split(",")
			.filter((id) => id.length > 0)
			.map(nameOf);
	};

	/**
	 * SIMS-8038 (FR-7) — the response carries a per-program breakdown, so the report
	 * renders a section PER PROGRAM, each under its own `ProgramHeading`.
	 *
	 * Declared here rather than beside `sortedBreakdown` below, where it used to sit,
	 * because the context line needs it and is built first. The two are the same
	 * number: `sortProgramBreakdown` maps the rows one-to-one, and the line only needs
	 * to know whether a breakdown exists — not the sorted, name-resolved array.
	 */
	const perProgram = (data?.programBreakdown?.length ?? 0) > 0;

	// Context subtitle shown under each section band (SIMS-5604) — the applied
	// filters, dot-separated, derived from the current selection.
	//
	// SIMS-8891: `programName` is the FIRST selected program, so once SIMS-8038 split
	// the report into a section per program it named one of four under a band covering
	// all of them. It now renders only in the combined layout, which has no program
	// heading of its own; `sectionContextLine` holds the rule and the reasoning.
	const contextLine = sectionContextLine(
		{
			programName,
			orgUnits: nameById(siteOptions, orgUnitIds),
			payers: nameById(payerOptions, payerIds),
			dateRange:
				startDate || endDate
					? `${startDate ? dayjs(startDate).format(dateFormat) : "…"} – ${
							endDate ? dayjs(endDate).format(dateFormat) : "…"
						}`
					: "",
		},
		perProgram,
	);

	// Semantic numeral color by result type (theme tokens, not raw hex).
	const bucketColor = (key: string, label: string): string => {
		const s = `${key} ${label}`.toLowerCase();
		if (/(unexcused|positive|missed|absent)/.test(s)) return c.bad;
		if (/excus/.test(s)) return c.warn;
		if (/(negative|attend)/.test(s)) return c.good;
		return c.ink;
	};

	// SIMS-8006: the Hi-Fi's "View programs" affordance on the exclusion banner.
	// Scrolls back to the filter bar, where the program chips show exactly which
	// programs are selected — the banner is deliberately cause-neutral, so it must
	// not itself name or single out the hidden ones.
	const filterBarRef = useRef<HTMLDivElement>(null);
	const viewPrograms = (): void =>
		filterBarRef.current?.scrollIntoView({
			behavior: "smooth",
			block: "start",
		});

	const clearFilters = (): void => {
		/**
		 * SIMS-8902 — "Clear all" clears the PROGRAM selection too.
		 *
		 * QA filed this against Visit Detail; the same code, with the same comment,
		 * was here. Fixed in both rather than only where it was reported: it is one
		 * defect on a control both reports share, and leaving this copy behind would
		 * mean the same button behaves differently between two tabs of the same
		 * dashboard.
		 *
		 * This used to re-select `programs[0]`, honouring SIMS-7390's invariant that
		 * "a program is always selected". That invariant belongs to the
		 * single-program report. Since SIMS-8038 split this one into a section per
		 * program, silently leaving one program behind means the button does not do
		 * what it says, and the user cannot tell a deliberate one-program selection
		 * from a leftover. SUPERSEDES SIMS-7390 for this tab too.
		 *
		 * Safe to clear to empty here for the same reasons as Visit Detail: the
		 * caller already drops the run snapshot, `canRun` is gated on a non-empty
		 * selection, and `NoProgramCard` renders the prompt — so the report lands in
		 * the same state as a freshly opened tab and issues no request.
		 */
		onProgramsChange?.([]);
		onProgramChange("");
		setOrgUnitIds([]);
		setPayerIds([]);
		setGroupLabelIds([]);
		setProviderId("");
		setStartDate("");
		setEndDate("");
	};

	// ---- presentational helpers -------------------------------------------
	// NOTE: this is a LOCAL FilterField, distinct from the one exported by ../common
	// (which ProgramFilterField and OrgUnitFilterField use). Both exist; this one owns
	// the plain filters in this tab's grid.
	const FilterField: FC<{
		label: string;
		multi?: boolean;
		/** Start a fresh grid row — pins the item to column 1 so the grid wraps. */
		newRow?: boolean;
		/** Muted sub-label under the control, e.g. "Grouped by program". */
		helperText?: string;
		children: ReactNode;
	}> = ({ label, multi, newRow, helperText, children }) => (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				gap: 1,
				minWidth: 0,
				...(newRow ? { gridColumn: { md: "1" } } : {}),
			}}
		>
			<Stack direction="row" spacing={1} alignItems="center">
				<Typography
					sx={{
						fontSize: 12,
						fontWeight: 600,
						letterSpacing: "0.06em",
						color: c.label,
					}}
				>
					{label.toUpperCase()}
				</Typography>
				{multi && (
					<Chip
						size="small"
						label="MULTI"
						sx={{ height: 18, fontSize: 10, fontWeight: 700 }}
						color="primary"
						variant="outlined"
					/>
				)}
			</Stack>
			{children}
			{/* Always rendered so fields with and without a sub-label stay the same
			    height and their controls keep sharing a baseline. */}
			<Typography sx={{ fontSize: 12, color: c.muted, minHeight: 18 }}>
				{helperText ?? ""}
			</Typography>
		</Box>
	);

	const multiInput = (
		value: string[],
		options: Option[],
		onChange: (v: string[]) => void,
		placeholder: string,
		disabled = false,
	): ReactNode => (
		<Select
			size="small"
			multiple
			displayEmpty
			disabled={disabled}
			value={value}
			input={<OutlinedInput />}
			onChange={(e) => {
				const v = e.target.value;
				onChange(typeof v === "string" ? v.split(",") : v);
			}}
			renderValue={(sel) =>
				(sel as string[]).length === 0 ? (
					<Typography sx={{ color: c.muted }}>{placeholder}</Typography>
				) : (
					<Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap" }}>
						{(sel as string[]).map((id) => (
							<Chip
								key={id}
								size="small"
								label={options.find((o) => o.id === id)?.name ?? id}
							/>
						))}
					</Box>
				)
			}
		>
			{options.map((o) => (
				<MenuItem key={o.id} value={o.id}>
					{o.name ?? o.id}
				</MenuItem>
			))}
		</Select>
	);

	// Tinted section band: uppercase section title + applied-filter context line.
	// SIMS-7770 (#4): an optional `note` clarifies which date each section's date
	// range filters on (counts=enrollment start, incentives=transaction, visits=
	// visit date) — it renders inside the PDF (the filter bar is excluded).
	const SectionBand: FC<{ title: string; note?: string }> = ({
		title,
		note,
	}) => (
		<Box
			sx={{
				bgcolor: c.band,
				px: 3,
				py: 2,
				borderBottom: `1px solid ${c.border}`,
			}}
		>
			<Typography
				sx={{
					fontSize: 13,
					fontWeight: 700,
					letterSpacing: "0.08em",
					color: c.label,
				}}
			>
				{title}
			</Typography>
			{contextLine && (
				<Typography sx={{ fontSize: 13, color: c.muted, mt: 0.5 }}>
					{contextLine}
				</Typography>
			)}
			{note && (
				<Typography
					sx={{ fontSize: 12, color: c.muted, mt: 0.5, fontStyle: "italic" }}
				>
					{note}
				</Typography>
			)}
		</Box>
	);

	/**
	 * 4-column metric row of CARDS; padded so the grid stays aligned.
	 *
	 * SIMS-8041: `total` switches the figures to the Hi-Fi's total treatment —
	 * weight 500 in --brand-primary-main, where a program section uses 400 in
	 * --text-primary. One flag rather than a second row component, so a total and a
	 * program section can never drift apart in layout.
	 *
	 * These were built as one seamless grid separated by hairline dividers. The Hi-Fi
	 * draws them as discrete cards — "Cards 12px + --shadow-1" — so each figure now
	 * carries its own border and radius. The distinction is not decorative: four
	 * bordered cards read as four independent measures, where a divided grid reads as
	 * one table whose columns are related.
	 */
	const MetricRow: FC<{ cells: Metric[]; total?: boolean }> = ({
		cells,
		total = false,
	}) => {
		const padded: Metric[] = [...cells];
		while (padded.length % 4 !== 0) padded.push({});
		return (
			<Box
				sx={{
					display: "grid",
					gridTemplateColumns: { xs: "repeat(2,1fr)", md: "repeat(4,1fr)" },
					// Section padding 20px 24px and a 16px gutter, per the Hi-Fi.
					gap: "16px",
					px: "24px",
					pb: "20px",
				}}
			>
				{padded.map((cell, i) =>
					// A padding cell holds the grid's shape and must stay invisible — a
					// bordered empty card would read as a metric that failed to load.
					cell.label == null ? (
						<Box key={i} />
					) : (
						<Box
							key={i}
							sx={{
								p: "16px 18px",
								borderRadius: "8px",
								/*
								 * BOTH card kinds are WHITE. An earlier pass filled the total
								 * cards with the brand tint, which is backwards: in the Hi-Fi the
								 * tint sits on the BAND behind them and the cards stay `#fff`,
								 * reading as the same four cards the program sections show, with
								 * different numbers. Filling them made the total look like a
								 * different component instead of a peer section.
								 *
								 * The total's border is the faint brand tone rather than the
								 * neutral one — the only card-level difference.
								 */
								border: `1px solid ${
									total ? theme.palette.primary.light : c.border
								}`,
								// The card face is the theme's lightest surface — never a literal
								// hex. Colours come from the account's theme settings, so a
								// hardcoded #fff would ignore a tenant's configured surface even
								// though the Hi-Fi writes `background: #fff` for the sims theme.
								bgcolor: theme.palette.surface.light,
							}}
						>
							{cell.label != null && (
								<>
									<Typography
										sx={{
											fontSize: 11,
											fontWeight: 500,
											letterSpacing: "0.08em",
											textTransform: "uppercase",
											color: c.muted,
											lineHeight: 1.35,
											// Two label lines' worth, so the figures line up across
											// cards whose labels wrap differently.
											minHeight: 30,
										}}
									>
										{cell.label}
									</Typography>
									<Typography
										data-total={total ? "true" : undefined}
										sx={{
											font: "400 32px/40px Roboto, system-ui, sans-serif",
											// The Hi-Fi puts every figure on tabular figures. Without
											// it the digits have different widths, so the four cards'
											// numbers do not align and each one changes width as its
											// value changes.
											fontVariantNumeric: "tabular-nums",
											fontWeight: total ? 500 : 400,
											// A total keeps the brand colour even when the cell asks for a
											// semantic one — the total/program distinction outranks it,
											// because it is the ONLY thing marking a total as a total.
											color: total
												? theme.palette.primary.main
												: (cell.color ?? c.ink),
											mt: "6px",
										}}
									>
										{cell.value}
									</Typography>
									{cell.caption && (
										<Typography sx={{ fontSize: 12, color: c.muted, mt: 0.25 }}>
											{cell.caption}
										</Typography>
									)}
								</>
							)}
						</Box>
					),
				)}
			</Box>
		);
	};

	/**
	 * SIMS-8041 AC1/AC2 — the "All selected programs" section that closes the
	 * Enrollment and Incentive panels.
	 *
	 * A PEER SECTION in card form, not a table footer: it repeats the panel's own
	 * four cards with the summed values. The band, the `functions` icon and the
	 * figure treatment are read from the Hi-Fi and are the only thing separating a
	 * total from a fourth program section.
	 *
	 * Rendered even with a single program selected (decision 2026-08-10) so the
	 * panel does not change shape as the selection crosses one.
	 *
	 * NOT used on Visit Results: SIMS-8056 AC3 requires that panel to STATE it has
	 * no total. Completing the pattern there would break that AC.
	 */
	const TotalSection: FC<{ cells: Metric[]; footnote?: string }> = ({
		cells,
		footnote,
	}) => (
		// --surface-brand-light in the Hi-Fi. It was on the neutral surface tint, which
		// left the band nearly indistinguishable from a program section — and the band
		// is the ONLY thing that marks these figures as a sum rather than a fifth
		// program.
		<Box sx={{ bgcolor: theme.palette.primary.light }}>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: "10px",
					px: "24px",
					pt: "20px",
					mb: "16px",
				}}
			>
				{/*
				 * The Hi-Fi names this icon `functions` — Material Symbols' Greek
				 * summation sign (Σ), which is the whole point: it says "this row is a
				 * sum" without words.
				 *
				 * It was written as <span className="material-symbols-outlined">functions
				 * </span>, a FONT LIGATURE. That font is not loaded anywhere in this app
				 * — this was its only use — so the ligature never resolved and the band
				 * rendered the literal word "functions" next to "All selected programs".
				 * MUI's SVG icon carries the same glyph with no font dependency.
				 */}
				<FunctionsIcon
					sx={{ fontSize: 20, color: theme.palette.primary.main }}
				/>
				<Typography
					sx={{
						fontWeight: 700,
						fontSize: 14,
						letterSpacing: "0.06em",
						textTransform: "uppercase",
						color: theme.palette.primary.main,
					}}
				>
					All selected programs
				</Typography>
			</Box>
			<MetricRow cells={cells} total />
			{/*
			 * SIMS-8048 AC3 — the footnote lives INSIDE the tinted band, not under it on
			 * white. It explains how the figure directly above it was computed, and a
			 * caption sitting outside the band reads as a note about the whole panel —
			 * which would be wrong: it qualifies the TOTAL, not the program sections.
			 */}
			{footnote && (
				<Typography
					sx={{
						fontSize: 12,
						lineHeight: "18px",
						color: c.muted,
						px: 3,
						pb: 2.5,
					}}
				>
					{footnote}
				</Typography>
			)}
		</Box>
	);

	// SIMS-8038 (FR-7): built from a counts object rather than closing over the
	// union, so a program section and the union render through the SAME code. The
	// labels are NOT changed here — the FR-9 relabel is SIMS-8048.
	/**
	 * SIMS-8048 AC1 (FR-9) — the four labels are FIXED STRINGS, quoted verbatim from
	 * the Hi-Fi and from the AC.
	 *
	 * They drop the `beneficiaryTerms` plural that the previous wording interpolated
	 * ("Number of Participants Enrolled"), so a tenant that renamed participants to
	 * "Members" no longer sees that term in these four cards. That is what the source
	 * and the AC both specify; recorded here because losing a tenant-configurable
	 * term inside a relabel ticket is the kind of change that reads as a regression
	 * if nobody wrote down that it was deliberate. `labels.plural` is still returned
	 * and still used elsewhere.
	 */
	const enrollmentCellsFor = (
		src: ProgramActivitySummaryResponse["enrollmentCounts"] | undefined,
	): Metric[] => [
		{ label: "Number of new enrollments", value: src?.enrolled ?? 0 },
		{ label: "Number of unique active members", value: src?.active ?? 0 },
		{ label: "Number of completed enrollments", value: src?.completed ?? 0 },
		{ label: "Number of disenrollments", value: src?.disenrolled ?? 0 },
	];

	const incentiveCellsFor = (
		src: ProgramActivitySummaryResponse["incentiveSummary"] | undefined,
	): Metric[] => [
		{
			label: "Total Earned",
			value: money(src?.totalEarned ?? 0),
			color: c.good,
		},
		{
			label: "Total Earned Transactions",
			value: src?.earnedTxnCount ?? 0,
			// SIMS-8905: the caption agrees with its count — "1 transaction", not
			// "1 transactions". SIMS-8038 defined the metric with a fixed caption, so
			// the same wording reached the screen and the PDF export alike.
			caption: txnCaption(src?.earnedTxnCount ?? 0),
		},
		{
			label: "Total Disbursed",
			value: money(src?.totalDisbursed ?? 0),
			color: c.blue,
		},
		{
			label: "Total Disbursed Transactions",
			value: src?.disbursedTxnCount ?? 0,
			caption: txnCaption(src?.disbursedTxnCount ?? 0),
		},
	];

	const enrollmentCells = enrollmentCellsFor(counts);
	const incentiveCells = incentiveCellsFor(inc);

	/**
	 * SIMS-8038 AC2 — alphabetical by program DISPLAY NAME. Sorted here rather than
	 * server-side: the response carries `programId` only, and this screen already
	 * resolves names via `programNameOf`. The same array drives all three panels, so
	 * the order is identical in every one of them, which is what AC2 requires.
	 *
	 * Empty when the API predates SIMS-8038 — the caller then keeps the single
	 * combined layout rather than rendering zero sections.
	 */
	const sortedBreakdown = useMemo(
		() =>
			sortProgramBreakdown(data?.programBreakdown, (id) =>
				programNameOf(programs, id),
			),
		[data?.programBreakdown, programs],
	);

	/*
	 * Section copy, hoisted out of the JSX so the PDF states it in the same words
	 * (SIMS-8062 / NFR-4). These notes are not decoration — each one tells the reader
	 * which date the panel filters on, and the Visit Results one says outright that the
	 * absence of a total is a decision. A PDF that dropped them would be a different,
	 * more confident document than the screen.
	 */
	const sectionNotes = {
		enrollment: perProgram
			? `${sortedBreakdown.length} programs · alphabetical. Date range filters by enrollment start date. Active counts enrollments whose active period overlaps the range.`
			: "Date range filters by enrollment start date. Active counts enrollments whose active period overlaps the range.",
		incentive: perProgram
			? `${sortedBreakdown.length} programs · alphabetical. Date range filters by transaction date.`
			: "Date range filters by transaction date.",
		visit: perProgram
			? `${sortedBreakdown.length} programs · alphabetical. No total — results are per event and not comparable across programs. Date range filters by visit date.`
			: "Date range filters by visit date.",
	};

	// SIMS-8048 AC3 — the methodological footnote. Verbatim from the Hi-Fi; the total is
	// a sum of per-program distincts, not a person count, and the PDF must say so too.
	const uniqueActiveFootnote =
		"Unique active members is the sum of each program's distinct count. A beneficiary active in more than one selected program is counted once per program.";

	const noVisitResultsText = "No visit results.";

	/**
	 * SIMS-8062 (FR-17) / NFR-4 — export the report as a STRUCTURED PDF.
	 *
	 * This used to rasterise the DOM with html2canvas and slice the image across pages.
	 * That met FR-17 and, once the capture was narrowed to the report body, it did mirror
	 * the screen — but it produced a picture: no selectable text, nothing searchable,
	 * nothing assistive technology can read, and megabytes per file. NFR-4 asks for a
	 * document, so the PDF is now typeset from the same values the screen renders.
	 *
	 * What that trades: it is no longer pixel-identical, because real glyphs reflow.
	 * What it keeps: the sections, their order, the headings, the copy, the numbers, the
	 * "All selected programs" bands and the AC3 footnote — everything the report SAYS.
	 *
	 * It also no longer touches the DOM, so the export does not depend on the report
	 * being laid out, scrolled into view, or even mounted at capture time.
	 */
	/**
	 * SIMS-8904 — one program's "Applied:" line.
	 *
	 * Extracted so the SCREEN's ProgramHeading and the PDF export read the same
	 * function. They were separate before: the screen stated the filters per
	 * section while the PDF carried a single line at the top of the document, so
	 * the export could not say which org units narrowed which program — and with
	 * one line bound to one program it named a program that a different run had
	 * excluded entirely.
	 */
	const programAppliedLine = (programId: string): string =>
		programContextLine(
			(data?.programFilters ?? []).find((f) => f.programId === programId),
			orgUnitNameOf,
			{
				payers: appliedNames(applied?.payerIds, payerOptions),
				groupLabels: appliedNames(applied?.groupLabelIds, groupLabelOptions),
			},
			// `null` means "nothing was narrowed"; the PDF prints nothing for an
			// empty line, so the two collapse to the same thing here.
		) ?? "";

	const handlePdf = async (): Promise<void> => {
		setIsExporting(true);
		try {
			// Loaded on demand: jsPDF and autoTable are heavy, and every user of this
			// dashboard pays for them at startup otherwise. The tab renders without them.
			const [{ default: jsPDF }, { default: autoTable }] = await Promise.all([
				import("jspdf"),
				import("jspdf-autotable"),
			]);

			const hidden = hiddenProgramCount(
				data?.programFilters,
				excludedProgramCount,
			);
			const model = buildProgramSummaryPdfModel({
				title: "Program Activity Summary",
				filters: contextLine,
				notice:
					hidden > 0
						? exclusionBannerText(hidden, effectivePrograms.length)
						: undefined,
				breakdown: sortedBreakdown.map((p) => ({
					programId: p.programId,
					programName: p.programName,
					// SIMS-8904 AC1: each program carries its OWN applied-filter line,
					// from the same helper the screen's heading uses.
					contextLine: programAppliedLine(p.programId),
					enrollmentCounts: p.enrollmentCounts,
					incentiveSummary: p.incentiveSummary,
					visitResultsByEvent: p.visitResultsByEvent,
				})),
				// The screen's own formatters, so a label or a currency mask can never
				// drift between the page and its export.
				enrollmentCellsFor: (src) =>
					toPdfMetrics(enrollmentCellsFor(src as never)),
				incentiveCellsFor: (src) =>
					toPdfMetrics(incentiveCellsFor(src as never)),
				enrollmentTotal: toPdfMetrics(enrollmentCells),
				incentiveTotal: toPdfMetrics(incentiveCells),
				enrollmentNote: sectionNotes.enrollment,
				incentiveNote: sectionNotes.incentive,
				visitNote: sectionNotes.visit,
				enrollmentFootnote: uniqueActiveFootnote,
				visitEmptyText: noVisitResultsText,
				combinedVisitResults: data?.visitResultsByEvent ?? [],
			});

			const doc = new jsPDF("portrait", "pt", "a4");
			renderProgramSummaryPdf(
				doc,
				model,
				{
					ink: c.ink,
					muted: c.muted,
					band: c.band,
					bandInk: c.label,
					total: theme.palette.primary.main,
					border: c.border,
				},
				autoTable,
			);
			doc.save("program_summary.pdf");
		} finally {
			setIsExporting(false);
		}
	};

	/**
	 * SIMS-8056 AC1 (FR-11) — one program's Visit Results TABLE.
	 *
	 * Replaces the KPI-card rendering SIMS-8038 left in place. Rows are events,
	 * columns are that program's bucket labels — per program, not global: the Hi-Fi
	 * shows Negative/Positive/Not tested for one program and Completed/Missed/Excused
	 * for another in the same report.
	 *
	 * A bucket an event does not define renders an em dash, never 0: zero would assert
	 * a measurement that returned nothing, where the truth is there is no such
	 * measurement for that event.
	 */
	const VisitResultsTable: FC<{
		events: ProgramActivitySummaryResponse["visitResultsByEvent"];
	}> = ({ events }) => {
		const columns = visitBucketColumns(events);
		if (columns.length === 0) return null;
		const cell = {
			px: 2,
			py: 1.5,
			borderBottom: `1px solid ${c.divider}`,
		} as const;
		return (
			<Box sx={{ px: 3, pb: 2 }}>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: `2fr ${columns.map(() => "1fr").join(" ")}`,
					}}
				>
					{/*
					 * The header row is BRAND-COLOURED, per the Hi-Fi. It was rendered in
					 * the muted label tone, which made the column names recede into the
					 * figures below them — and these headers carry meaning the figures
					 * cannot: the bucket set changes per program (Negative/Positive/Not
					 * tested here, Completed/Missed/Excused there), so the reader has to
					 * notice them to read the row correctly.
					 */}
					{["Event", ...columns].map((heading, i) => (
						<Typography
							key={heading}
							sx={{
								...cell,
								fontSize: 12,
								fontWeight: 700,
								letterSpacing: "0.04em",
								textTransform: "uppercase",
								color: theme.palette.primary.main,
								bgcolor: theme.palette.primary.light,
								borderBottom: `2px solid ${theme.palette.primary.main}`,
								textAlign: i === 0 ? "left" : "right",
							}}
						>
							{heading}
						</Typography>
					))}
					{events.map((ev) => {
						const name = ev.eventName ?? "Event";
						return [
							<Typography
								key={`${ev.eventId}-name`}
								sx={{ ...cell, color: c.ink }}
							>
								{name}
							</Typography>,
							...visitRowFor(ev, columns).map((value, i) => (
								<Typography
									key={`${ev.eventId}-${columns[i]}`}
									sx={{
										...cell,
										textAlign: "right",
										color:
											value === null
												? c.muted
												: bucketColor(columns[i], columns[i]),
									}}
								>
									{value === null ? "—" : value.toLocaleString("en-US")}
								</Typography>
							)),
						];
					})}
				</Box>
			</Box>
		);
	};

	/**
	 * Program section header, read from the Hi-Fi itself (fetched 2026-08-10 via
	 * DesignSync, the first time the source of record was reachable):
	 *
	 *   [▌] Bridge Recovery Center                            3 org units
	 *   Applied: Statewide Lab Network · … · Payer: Medicaid, Blue Cross
	 *
	 * Three parts, not one. The first version of this shipped only the name — the
	 * `N org units` count and the per-section `Applied:` line were both missing, and
	 * the earlier transcription of the design did not show them inside the section.
	 *
	 * The count comes from `programFilters[].orgUnitCount`, which SIMS-8014 already
	 * returns for exactly this sub-header. The `Applied:` line reuses
	 * `programContextLine()` — the same projection of the real query the standalone
	 * SIMS-8014 block renders, so the two can never disagree.
	 */
	const ProgramHeading: FC<{ name: string; programId: string }> = ({
		name,
		programId,
	}) => {
		// SIMS-8062: a program heading is where the PDF may start a new page —
		// breaking here keeps a program's cards together instead of slicing one.
		const effective = (data?.programFilters ?? []).find(
			(f) => f.programId === programId,
		);
		// SIMS-8904: the SAME helper the PDF export uses, so the screen and the
		// document can never state different filters for the same program.
		const appliedLine = programAppliedLine(programId);
		return (
			<Box sx={{ px: "24px", pt: "20px" }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
					<Box
						sx={{
							width: "4px",
							height: "18px",
							borderRadius: "2px",
							bgcolor: theme.palette.primary.main,
						}}
					/>
					<Typography
						sx={{
							font: "700 16px Roboto, system-ui, sans-serif",
							color: c.ink,
						}}
					>
						{name}
					</Typography>
					{/*
					 * The Hi-Fi's org-unit count PILL, and it sits beside the program name
					 * rather than pushed to the far edge of the row. Both matter: at the far
					 * edge, on a wide viewport, the count is separated from the name it
					 * qualifies by a stretch of empty space and reads as a column of its own.
					 */}
					{effective && !effective.unrestricted && (
						<Chip
							size="small"
							label={`${effective.orgUnitCount} ${
								effective.orgUnitCount === 1 ? "org unit" : "org units"
							}`}
							sx={{
								height: "auto",
								borderRadius: "999px",
								// The SECONDARY brand tone, not the primary one — in the Hi-Fi
								// this pill is the only element in the heading that is not
								// brand-primary, which is what keeps it from competing with the
								// spine and the program name beside it.
								bgcolor: theme.palette.secondary.light,
								color: theme.palette.secondary.dark,
								fontSize: 11,
								fontWeight: 500,
								letterSpacing: "0.06em",
								textTransform: "uppercase",
								"& .MuiChip-label": { px: "10px", py: "4px" },
							}}
						/>
					)}
				</Box>
				{appliedLine && (
					<Typography
						sx={{
							font: "400 13px/20px Roboto, system-ui, sans-serif",
							color: c.muted,
							m: "6px 0 16px 14px",
						}}
					>
						{appliedLine}
					</Typography>
				)}
			</Box>
		);
	};

	const sectionPaper = {
		borderColor: c.border,
		borderRadius: 3,
		overflow: "hidden",
	} as const;

	return (
		<Stack spacing={3}>
			{/* Page header — title + breadcrumb + Export to PDF */}
			<Paper
				variant="outlined"
				sx={{
					borderColor: c.border,
					borderRadius: 3,
					p: 3,
					display: "flex",
					alignItems: "flex-start",
					justifyContent: "space-between",
					gap: 2,
					flexWrap: "wrap",
				}}
			>
				<Box>
					<Typography sx={{ fontSize: 26, fontWeight: 700, lineHeight: 1.1 }}>
						Program Activity Summary
					</Typography>
					<Typography sx={{ fontSize: 14, color: c.muted, mt: 0.75 }}>
						Reports / Summary
					</Typography>
				</Box>
				<Button
					variant="outlined"
					startIcon={
						isExporting ? (
							<CircularProgress size={16} color="inherit" />
						) : (
							<FileDownloadOutlinedIcon />
						)
					}
					disabled={isExporting}
					onClick={handlePdf}
				>
					{isExporting ? "Exporting…" : "Export to PDF"}
				</Button>
			</Paper>

			{/* Filter bar card (excluded from the PDF capture) */}
			<Paper
				ref={filterBarRef}
				variant="outlined"
				sx={{ borderColor: c.border, borderRadius: 3, p: 3 }}
			>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: {
							xs: "1fr",
							sm: "repeat(2,1fr)",
							// Four equal columns, matching Enrollment Summary. The old
							// trailing 112px column existed for the narrow Clear button and
							// squeezed Run report; the actions now share the normal columns.
							md: "repeat(4,1fr)",
						},
						gap: 3,
						alignItems: "end",
					}}
				>
					<ProgramFilterField
						programIds={programIds ?? []}
						programs={programs}
						onProgramsChange={onProgramsChange}
						labelColor={c.label}
						mutedColor={c.muted}
						errorColor={theme.palette.error.main}
					/>
					{/*
					 * Grouped panel (SIMS-7994 AC2): shared org units first with their
					 * "in N programs" count, then one group per selected program.
					 */}
					<OrgUnitFilterField
						userPrograms={userInfo?.programs}
						selectedProgramIds={effectivePrograms}
						programs={programs}
						options={siteOptions}
						value={orgUnitIds}
						onChange={(ids) => {
							setOrgUnitIds(ids);
							// AC3: editing any filter clears the results until Run again.
							setApplied(null);
						}}
						labelColor={c.label}
						mutedColor={c.muted}
					/>
					{/* Sub-label from the hand-off — the options span the selected programs. */}
					<FilterField label="Payer" multi helperText="Grouped by program">
						{multiInput(
							payerIds,
							payerOptions,
							changePayerIds,
							payersDisabled ? "No payers for this program" : "All payers",
							payersDisabled,
						)}
					</FilterField>
					<FilterField label="Group labels" helperText="Grouped by program">
						{multiInput(
							groupLabelIds,
							groupLabelOptions,
							changeGroupLabelIds,
							"All",
						)}
					</FilterField>
					<FilterField label="Provider">
						<Select
							size="small"
							displayEmpty
							value={providerId}
							input={<OutlinedInput />}
							onChange={(e) => changeProviderId(e.target.value)}
							renderValue={(v) =>
								v ? (
									(providerOptions.find((o) => o.id === v)?.name ?? v)
								) : (
									<Typography sx={{ color: c.muted }}>All providers</Typography>
								)
							}
						>
							<MenuItem value="">All providers</MenuItem>
							{providerOptions.map((o) => (
								<MenuItem key={o.id} value={o.id}>
									{o.name ?? o.id}
								</MenuItem>
							))}
						</Select>
					</FilterField>
					{/* newRow: the dates start a fresh row, per the requested layout. */}
					<FilterField label="Start date" newRow>
						{/* SIMS-7770 (#5): MUI-X picker accepts a typed 4-digit year.
						    disableFuture={false} preserves the old native input; isValid()
						    guards the typed-invalid state (avoids "Invalid Date"). */}
						<BasicDatepicker
							label="Start date"
							format={dateFormat}
							disableFuture={false}
							value={startDate ? dayjs(startDate) : null}
							onChange={(d) =>
								changeStartDate(d && d.isValid() ? d.format("YYYY-MM-DD") : "")
							}
						/>
					</FilterField>
					<FilterField label="End date">
						<BasicDatepicker
							label="End date"
							format={dateFormat}
							disableFuture={false}
							value={endDate ? dayjs(endDate) : null}
							onChange={(d) =>
								changeEndDate(d && d.isValid() ? d.format("YYYY-MM-DD") : "")
							}
						/>
					</FilterField>
					{/*
					 * Clear all + Run report — last cell of the grid, right-aligned.
					 * The hand-off pairs them here: Clear all as a text link and Run
					 * report as the brand action, not a single dark Clear button.
					 */}
					{/*
					 * No wrapper: ReportActions renders TWO grid cells so Clear all and
					 * Run report each get their own column. Wrapping them would collapse
					 * both into the 112px last column, where they wrapped and looked
					 * broken.
					 */}
					<ReportActions
						onClearAll={() => {
							clearFilters();
							setApplied(null);
						}}
						onRun={runReport}
						// SIMS-9292 AC5: a program always applies (D4), so the
						// program filter no longer gates the run — only waiting for
						// the assigned set does, or a run would snapshot no programs.
						canRun={!programsLoading}
					/>
				</Box>
			</Paper>

			{/* Reserve the bar's height so the content doesn't jump on refetch. */}
			<Box sx={{ height: 4 }}>
				{(isLoading || isFetching) && <LinearProgress />}
			</Box>

			{/*
			 * SIMS-8062 AC2 — the export contains no controls, BY CONSTRUCTION.
			 *
			 * It used to rasterise this subtree and exclude the filter bar and buttons
			 * with data-html2canvas-ignore. That worked, but narrowly: html2canvas skips
			 * an ignored element's CONTENT while its LAYOUT SPACE remains, so while the
			 * capture wrapped the whole page the PDF came out with a hole where the
			 * Export button was and a blank band where the filter bar was.
			 *
			 * The structured export (NFR-4) reads none of this DOM — it is built from the
			 * response, so a control cannot reach it whatever the markup does. The ignore
			 * markers are gone with the mechanism that used them; keeping them would
			 * advertise a safeguard no longer wired to anything.
			 */}
			<Stack spacing={3}>
				{/*
				 * SIMS-8006 AC1: the count is the SERVER's — how many selected programs
				 * actually contributed nothing after narrowing. `excludedProgramCount` stays
				 * only as the fallback for an API that predates SIMS-8014; it knows about
				 * org-unit exclusion alone and misses every other zero-row cause, so it
				 * under-reports.
				 */}
				<ExclusionBanner
					hiddenCount={hiddenProgramCount(
						data?.programFilters,
						excludedProgramCount,
					)}
					selectedCount={effectivePrograms.length}
					onViewPrograms={viewPrograms}
				/>

				{!programsLoading && !hasAssignedPrograms(assignedIds) && (
					<NoProgramCard
						borderColor={c.border}
						mutedColor={c.muted}
						variant="no-assigned-programs"
					/>
				)}

				{/*
				 * AC1: with a selection but no run yet, show the pre-run prompt rather than
				 * an empty report — nothing is fetched until Run report.
				 *
				 * Gated on the RESOLVED selection (SIMS-9292, D4): the report opens with an
				 * empty selection meaning every assigned program (Q-11 = A), and read raw
				 * that default never got the prompt. Still empty for a user with NO
				 * programs, who gets NoProgramCard above instead.
				 */}
				{effectivePrograms.length > 0 && !applied && (
					<Paper
						variant="outlined"
						sx={{
							borderColor: c.border,
							borderRadius: 3,
							py: 9,
							px: 3,
							textAlign: "center",
						}}
					>
						<Typography sx={{ fontSize: 20, mt: 1 }}>
							Select Run report to generate results
						</Typography>
						<Typography sx={{ color: c.muted, mt: 1 }}>
							All programs you are assigned to and the default filters are
							pre-selected. Changing any filter clears the results.
						</Typography>
					</Paper>
				)}

				{applied && (
					<>
						{/*
						 * SIMS-8038 AC1 — one section per program in each panel, in the SAME
						 * alphabetical order (AC2). `perProgram` is false against an API that
						 * predates SIMS-8038, and the single combined layout is kept: a missing
						 * key must not render as "no programs contributed".
						 */}

						{/* Enrollment summary */}
						<Paper variant="outlined" sx={sectionPaper}>
							<SectionBand
								title="ENROLLMENT SUMMARY"
								note={sectionNotes.enrollment}
							/>
							{perProgram ? (
								sortedBreakdown.map((p) => (
									<Box key={p.programId}>
										<ProgramHeading
											name={p.programName}
											programId={p.programId}
										/>
										<MetricRow cells={enrollmentCellsFor(p.enrollmentCounts)} />
									</Box>
								))
							) : (
								<MetricRow cells={enrollmentCells} />
							)}
							{/*
							 * SIMS-8041 AC1 — the total repeats the panel's own four cards,
							 * summed. Fed by the response's scalar `enrollmentCounts`, which
							 * SIMS-8038 kept as exactly this union and pinned with a
							 * `union === sum(buckets)` service test.
							 */}
							{/*
							 * SIMS-8048 AC3 — the methodological footnote rides INSIDE the total
							 * band, verbatim from the Hi-Fi.
							 *
							 * It is not decoration: the total is the SUM of each program's distinct
							 * count, so a beneficiary active in two selected programs is counted
							 * twice in it. Without this line the figure reads as a person count,
							 * which it is not — and that misreading is the whole of COMPLIANCE C-2.
							 */}
							{perProgram && (
								<TotalSection
									cells={enrollmentCells}
									footnote={uniqueActiveFootnote}
								/>
							)}
						</Paper>

						{/* Incentive summary */}
						<Paper variant="outlined" sx={sectionPaper}>
							<SectionBand
								title="INCENTIVE SUMMARY"
								note={sectionNotes.incentive}
							/>
							{perProgram ? (
								sortedBreakdown.map((p) => (
									<Box key={p.programId}>
										<ProgramHeading
											name={p.programName}
											programId={p.programId}
										/>
										<MetricRow cells={incentiveCellsFor(p.incentiveSummary)} />
									</Box>
								))
							) : (
								<MetricRow cells={incentiveCells} />
							)}
							{/* SIMS-8041 AC2 — same treatment, the Incentive panel's four cards. */}
							{perProgram && <TotalSection cells={incentiveCells} />}
						</Paper>

						{/*
						 * Visit results — one KPI group per configured visit event.
						 *
						 * SIMS-8038 AC1 adds the per-program SECTIONS. The card→table
						 * conversion the Hi-Fi asks for is SIMS-8056, deliberately not done
						 * here: doing both at once would duplicate that story's work.
						 */}
						<Paper variant="outlined" sx={sectionPaper}>
							<SectionBand
								title="VISIT RESULTS"
								/*
								 * SIMS-8056 AC3 — the absence of a total is STATED, not silent.
								 * Enrollment and Incentive both close with an "All selected
								 * programs" band (SIMS-8041), so saying nothing here reads as a
								 * bug rather than a decision. Copy verbatim from the Hi-Fi.
								 */
								note={sectionNotes.visit}
							/>
							{perProgram ? (
								sortedBreakdown.map((p) => (
									<Box key={p.programId}>
										<ProgramHeading
											name={p.programName}
											programId={p.programId}
										/>
										{p.visitResultsByEvent.length === 0 ? (
											// SIMS-8056 AC2 — a program with no results still gets its
											// section; omitting it would be indistinguishable from being
											// EXCLUDED, which the SIMS-8006 banner reports separately.
											// Copy is the source's, shorter than the build's previous
											// "…for the selected filters."
											<Typography sx={{ color: c.muted, px: 3, pb: 3 }}>
												{noVisitResultsText}
											</Typography>
										) : (
											<VisitResultsTable events={p.visitResultsByEvent} />
										)}
									</Box>
								))
							) : (data?.visitResultsByEvent ?? []).length === 0 ? (
								<Typography sx={{ color: c.muted, px: 3, py: 3 }}>
									{noVisitResultsText}
								</Typography>
							) : (
								<VisitResultsTable events={data?.visitResultsByEvent ?? []} />
							)}
						</Paper>
					</>
				)}
			</Stack>
		</Stack>
	);
};

export default ProgramActivitySummaryTab;
